import { AfterViewInit, Component, ElementRef, EventEmitter, OnDestroy, OnInit, Output, Pipe, PipeTransform, Renderer2, ViewChild, forwardRef } from '@angular/core';
import {FlowBuilderService} from '../../services/flowBuilder/flow-builder.service';
import {
    FindAndReplace, Found, Matched, ReplaceAllSteps, SearchError,
    SearchSettings
} from '../../services/flowBuilder/findAndReplace/FindAndReplace';
import {fromEvent, interval, Subscription} from "rxjs";
import {debounce, map, take, tap} from "rxjs/operators";
import { UntypedFormControl, UntypedFormGroup, ReactiveFormsModule } from "@angular/forms";
import {Store} from "@ngrx/store";
import {selectNodeTypesMap} from "../../../../core";
import {swalMini} from "../../../../shared/utils/utils";
import {animate, state, style, transition, trigger} from "@angular/animations";
import {RolesService} from "../../../../core/services/roles/roles.service";
import { ResizeDirective } from '../../../../shared/directives/resize/resize.directive';
import { MatSelect, MatOptgroup, MatOption } from '@angular/material/select';
import { MatDivider } from '@angular/material/divider';
import { NgTemplateOutlet } from '@angular/common';
import { LoadingIconComponent } from '../../../../shared/components/loading-icon/loading-icon.component';

const FIND_AND_REPLACE: { [index: string]: { display: string, value: string }[] } = {
    findAndReplace: [
        {
            display: "Find",
            value: "find"
        },
        {
            display: "Find & Replace",
            value: "findAndReplace"
        }
    ],
    settings: [
        {
            display: "All",
            value: "all"
        },
        {
            display: "Step Number",
            value: "stepNumber"
        },
        {
            display: "Variable",
            value: "variable"
        }
    ],
    options: [
        {
            display: "Match Case",
            value: "caseSensitive"
        },
        {
            display: "Whole Words",
            value: "wordMatch"
        }
    ]
}

export interface MatchClickEvent {
    stepId: string,
    stepType: string,
    nodeType: string,
    matched: Matched
    found: Found
}

export interface ReplaceStep {
    stepId: string,
    stepType: string,
    nodeType: string,
    step: any
}

@Component({
    selector: 'app-left-search-bar',
    templateUrl: './left-search-bar.component.html',
    styleUrls: ['./left-search-bar.component.scss'],
    animations: [
        trigger('state', [
            state('open', style({
                width: '180px',
            })),
            state('close', style({
                width: '0',
            })),
            transition('close <=> open', animate(500)),
        ]),
    ],
    imports: [ResizeDirective, ReactiveFormsModule, MatSelect, MatOptgroup, MatOption, MatDivider, NgTemplateOutlet, LoadingIconComponent, forwardRef(() => resultCountPipe), forwardRef(() => settingsDisplayPipe), forwardRef(() => stepNumberPipe), forwardRef(() => nodeIconPipe), forwardRef(() => wordEclipsePipe)]
})
export class LeftSearchBarComponent implements OnInit, OnDestroy, AfterViewInit {

    readonly findAndReplaceOptions: { [index: string]: { display: string, value: string }[] } = FIND_AND_REPLACE

    readonly groups = Object.keys(this.findAndReplaceOptions);

    @ViewChild("searchInput") searchInput: ElementRef;
    @Output("onClick") click: EventEmitter<MatchClickEvent> = new EventEmitter();
    @Output("onClose") closeClicked: EventEmitter<undefined> = new EventEmitter();
    @Output("onReplace") replaceClicked: EventEmitter<ReplaceStep> = new EventEmitter();
    @Output("onReplaceAll") replaceAllClicked: EventEmitter<ReplaceAllSteps[]> = new EventEmitter();
    @Output("onUndoAll") undoAllClicked: EventEmitter<ReplaceAllSteps[]> = new EventEmitter();

    findAndReplace: FindAndReplace;
    searchInputSubscription: Subscription;

    settingsForm: UntypedFormGroup;

    isSearch: boolean = true;
    found: Found[] | SearchError | { error: string, undo: boolean };
    replaced: Found[] = [];
    isLoading = false;
    searchFor: string;
    currentNodeType: {};
    selectedIndex: string = undefined;
    selected: {
        found: Found;
        matched: Matched
    }
    foundIndexed: { [index: string]: MatchClickEvent }

    state: "close" | "open" = 'close';

    canReplace = true;

    constructor(private flowBuilderService: FlowBuilderService,
                private store: Store,
                private renderer: Renderer2,
                private rolesService: RolesService) {
    }

    ngOnInit() {

        this.canReplace = this.rolesService.canEditFlows();

        this.findAndReplace = new FindAndReplace(this.flowBuilderService.getFlow());

        this.settingsForm = new UntypedFormGroup({
            settings: new UntypedFormControl(['find', 'all'])
        })

        this.store.select(selectNodeTypesMap)
            .pipe(take(1))
            .subscribe(nodeObjects => this.currentNodeType = nodeObjects);
        setTimeout(() => {
            this.state = 'open';
        })

        this.sizeChange("180px");
    }

    ngOnDestroy(): void {
        this.searchInputSubscription.unsubscribe();
    }

    ngAfterViewInit(): void {
        this.searchInputSubscription = fromEvent(this.searchInput.nativeElement, 'input')
            .pipe(
                tap(() => this.isLoading = true),
                debounce(_ => interval(500)),
                map((event: InputEvent) => event.target['value']),
                tap((searchFor: string) => this.searchFor = searchFor),
                tap((searchFor: string) => {
                    this.search(this.settingsForm.get('settings').value);
                    this.isLoading = false;
                    this.click.emit(undefined);
                })
            )
            .subscribe();
        this.searchInput.nativeElement.focus();
        this.addOpacityToAllNodes()
    }

    public updateFlow(): void {
        if (this.findAndReplace) {
            this.findAndReplace.setFlow(this.flowBuilderService.getFlow());
        }
    }

    close(): void {
        this.state = 'close';
        this.removeOpacityFromAllNode();
        this.flowBuilderService.setDefaultStyles();
        this.sizeChange("180px");
        setTimeout(() => this.closeClicked.emit(), 500)
    }

    public search(settings: string[]): void {
        this.found = this.findAndReplace.search(this.searchFor, this.getSearchSettings(settings));
        this.clearSelected();
        if (this.found && !this.found['error']) {
            this.calculateIndex(this.found as Found[]);
        }
    }

    public optionClicked(event: {
        isUserInput: any;
        source: { value: any; selected: any };
    }): void {
        if (event.isUserInput) {
            const value = event.source.value;
            const selected: string[] = this.settingsForm.get("settings").value;

            if (event.source.selected) {
                this.optionSelected(value, selected);
            } else {
                this.optionDeselection(value, selected);
            }
        }
    }

    public settingsChange(values: string[]) {
        if (this.searchFor) {
            this.search(values);
        }
    }

    private optionSelected(value: string, selected) {
        const findAndReplaceSwitch = (value) => {
            const fr = this.findAndReplaceOptions['findAndReplace']
                .map(fr => fr.value)
                .filter(fr => fr !== value);

            return selected.filter(s => !fr.includes(s))
        }

        const settingsSwitch = (value) => {
            const settings = this.findAndReplaceOptions['settings']
                .map(settings => settings.value)
                .filter(setting => setting !== value)

            return selected.filter(s => !settings.includes(s))
        }

        let settings = selected;

        switch (value) {
            case "find":
                settings = findAndReplaceSwitch(value);
                settings.push("all")
                this.isSearch = true;
                break;
            case "findAndReplace":
                const searchSettings = this.findAndReplaceOptions['settings'].map(fr => fr.value)
                settings = findAndReplaceSwitch(value).filter(setting => {
                    return !searchSettings.includes(setting)
                })
                this.isSearch = false;
                break;
            case "all":
            case "stepNumber":
            case "variable":
                settings = settingsSwitch(value);
                break;
        }

        this.settingsForm.get("settings").setValue(settings);

        setTimeout(() => {
            settings.push(value)
            this.settingsForm.get("settings").setValue(settings);
        })
    }

    private optionDeselection(value: string, selected) {
        switch (value) {
            case "find":
            case "findAndReplace":
            case "all":
            case "stepNumber":
            case "variable":
                this.settingsForm.get("settings").setValue(selected);
                break
        }
    }

    private getSearchSettings(settings: string[]): SearchSettings {
        const searchSettings: SearchSettings = {}

        settings.forEach(selected => {
            switch (selected) {
                case "wordMatch":
                case "variable":
                case "stepNumber":
                case "caseSensitive":
                    searchSettings[selected] = true
                    break
            }
        })

        return searchSettings
    }

    public nextItem(): void {
        const next = (index, length) => {
            ++index;
            if (index > length) {
                index = 0;
            }
            return index
        }

        this.nextOrPreviousItem(next);
    }

    public previousItem(): void {

        const previous = (index, length) => {
            --index;
            if (index < 0) {
                index = length;
            }
            return index;
        }

        this.nextOrPreviousItem(previous);
    }

    private nextOrPreviousItem(funcIndex: (index: number, length: number) => number) {
        if (!this.foundIndexed) {
            this.calculateIndex(this.found as Found[]);
        }

        const foundIndexKeys: string[] = Object.keys(this.foundIndexed);

        if (!this.selectedIndex) {
            this.selectedIndex = foundIndexKeys[0];
        } else {
            document.getElementById(this.selectedIndex).className = 'found-container-match';
            const index: number = funcIndex(foundIndexKeys.indexOf(this.selectedIndex), foundIndexKeys.length - 1);
            this.selectedIndex = foundIndexKeys[index];
        }

        document.getElementById(this.selectedIndex).className = 'found-container-match selected';

        const clickEvent: MatchClickEvent = this.foundIndexed[this.selectedIndex];

        this.selected = {
            found: clickEvent.found,
            matched: clickEvent.matched
        };

        this.click.emit(clickEvent);
    }

    private calculateIndex(found: Found[]) {
        this.foundIndexed = found.map((f, foundIndex) =>
            f.matched.map((m, matchedIndex) => (
                {
                    [`match-${foundIndex}-${matchedIndex}`]: {
                        matched: m,
                        found: f,
                        stepType: f.stepType,
                        nodeType: f.nodeType,
                        stepId: f.stepId
                    }
                }
            ))
        )
            .reduce((previousValue, currentValue) => [...previousValue, ...currentValue], [])
            .reduce((previousValue, currentValue) => ({...previousValue, ...currentValue}), {})
    }

    public onClick(found: Found, matched?: Matched, fIndex?: number, mIndex?: number) {
        if (this.selectedIndex) {
            document.getElementById(this.selectedIndex).className = 'found-container-match';
        }

        this.selectedElement(fIndex, mIndex)
        if (matched) {
            this.selected = {
                found,
                matched
            };
        } else {
            this.clearSelected();
        }

        this.removeOpacityOnSelectedNode(found.stepId)

        this.click.emit({
            stepId: found.stepId,
            stepType: found.stepType,
            nodeType: found.nodeType,
            matched,
            found
        });
    }


    private selectedElement(foundIndex: number, matchIndex: number): void {
        if (foundIndex === undefined && matchIndex === undefined) {
            this.selectedIndex = undefined;
            return;
        }

        const id: string = `match-${foundIndex}-${matchIndex}`
        document.getElementById(id).className = 'found-container-match selected'
        this.selectedIndex = id;
    }

    public replace(replaceWith: string): void {
        if (!this.selected) {
            swalMini({
                title: 'No result select to be replaced',
                type: 'warning',
            });
            return;
        }

        const {stepId, stepType, nodeType} = this.selected.found

        const matched: Matched = this.selected.matched;

        this.removeFoundResult(matched);
        this.calculateIndex(this.found as Found[]);

        const step = this.findAndReplace.replace(stepId, this.searchFor, replaceWith, matched, this.getSearchSettings(this.settingsForm.get('settings').value));

        this.clearSelected();

        this.replaceClicked.emit({
            stepId,
            stepType: stepType,
            nodeType: nodeType,
            step
        })
    }

    public replaceAll(replaceWith: string): void {
        if (this.found['error']) {
            return
        }

        const found: Found[] = this.found as Found[]

        const steps: ReplaceAllSteps[] = this.findAndReplace.replaceAll(this.searchFor, replaceWith, found, this.getSearchSettings(this.settingsForm.get('settings').value))

        this.found = {
            error: "All results replaced",
            undo: true
        }

        this.replaced = found;
        this.replaceAllClicked.emit(steps)
    }

    public undoAll() {
        const flow = this.findAndReplace.undoAll();

        const steps: ReplaceAllSteps[] = this.replaced.map(item => ({
                stepId: item.stepId,
                step: flow[item.stepId],
                found: undefined
            }
        ));

        this.found = this.replaced;
        this.replaced = [];

        this.undoAllClicked.emit(steps)
    }

    private clearSelected(): void {
        this.selected = undefined;
        this.selectedIndex = undefined;
    }

    private removeOpacityOnSelectedNode(target: string) {
        const element = document.getElementById(target);

        const elements = Array.from(document.getElementsByClassName('searchSelectedNode'));
        this.addOpacity(elements);

        if (element) {
            this.renderer.removeClass(element, 'notExecutedNode');
            this.renderer.addClass(element, 'searchSelectedNode');
        }
    }

    private removeFoundResult(matched: Matched): void {
        const found: Found[] = this.found as Found[]
        const {stepId} = this.selected.found
        const path: string = matched.path.toString()

        this.found = found.filter(found => {
            if (found.stepId !== stepId) {
                return true
            }
            if (found.matched.length === 1) {
                return false;
            }
            found.matched = found.matched.filter(m => path != m.path.toString())
            return true;
        })
    }

    private removeOpacityFromAllNode() {
        const elements = Array.from(document.getElementsByClassName('node'));
        for (const element of elements) {
            if (typeof element === 'object') {
                this.renderer.removeClass(element, 'notExecutedNode');
                this.renderer.removeClass(element, 'searchSelectedNode');
            }
        }
    }

    private addOpacityToAllNodes() {
        const elements = Array.from(document.getElementsByClassName('node'));
        this.addOpacity(elements);
        this.flowBuilderService.setUnexecutedStyles();
    }

    private addOpacity(elements: Element[]) {
        for (const element of elements) {
            if (typeof element === 'object') {
                this.renderer.addClass(element, 'notExecutedNode');
                this.renderer.removeClass(element, 'searchSelectedNode');
            }
        }
    }

    public sizeChange(width: string) {
        document.getElementById('left-side-bar').style.width = width
    }

}

@Pipe({ name: 'settingsDisplay' })
export class settingsDisplayPipe implements PipeTransform {
    transform(settings: string[]) {
        const display: string[] = [];
        const find = (option, setting) => option.value === setting;
        for (const setting of settings) {
            switch (setting) {
                case "wordMatch":
                case "variable":
                case "stepNumber":
                case "caseSensitive":
                    const s = FIND_AND_REPLACE['settings'].find(option => find(option, setting));
                    if (s) {
                        display.push(s.display)
                        continue
                    }

                    const o = FIND_AND_REPLACE['options'].find(option => find(option, setting))
                    if (o) {
                        display.push(o.display)
                    }
                    break
            }
        }
        return display;
    }
}

@Pipe({ name: 'resultCount' })
export class resultCountPipe implements PipeTransform {
    transform(found: Found[]) {
        let count = 0;
        for (const f of found) {
            for (const m of f.matched) {
                ++count
            }
        }
        if (count) {
            return count
        }
        return found.length;
    }
}

@Pipe({ name: 'stepNumber' })
export class stepNumberPipe implements PipeTransform {
    transform(step: string) {
        return step.replace("step", "Step ");
    }
}

@Pipe({ name: 'nodeType' })
export class nodeTypePipe implements PipeTransform {
    transform(nodeType: string, nodeTypes: {}) {
        return nodeTypes[nodeType].title
    }
}

@Pipe({ name: 'nodeIcon' })
export class nodeIconPipe implements PipeTransform {
    transform(nodeType: string, nodeTypes: {}) {
        return nodeTypes[nodeType].image.primary
    }
}

@Pipe({ name: 'wordEclipse' })
export class wordEclipsePipe implements PipeTransform {
    transform(found: string, parentWidth: string) {

        const width = +parentWidth.replace("px", "");
        const wordStart = '<span class="highlight-text">';
        const wordEnd = '</span>';
        const eclipse_buffer = 3;

        const maxChar = Math.ceil(width / 8);

        const startIndex = found.indexOf(wordStart);
        const endIndex = found.indexOf(wordEnd);
        const wordLength = (endIndex - startIndex) - wordStart.length;

        if (maxChar > startIndex + wordLength + eclipse_buffer) {
            return found;
        }

        if (wordLength > maxChar) {
            return found.slice(startIndex, -1)
        }

        const offset = Math.floor(maxChar / 2) - Math.ceil(wordLength / 2) - eclipse_buffer;

        const start = (startIndex - offset);

        return `...${found.slice(start, -1)}`
    }
}
